import React, { useState, useEffect, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useParams, useNavigate } from 'react-router-dom';
import { FaUpload, FaFileAlt, FaPlus, FaEllipsisH } from 'react-icons/fa';
import { Printer, Wallet, LayoutGrid, ChevronLeft, Trash2, TrendingUp } from 'lucide-react';
import axiosInstance from '../../utils/axiosConfig';
import {
  AreaChart, Area, XAxis, Tooltip, ResponsiveContainer
} from 'recharts';
import toast from 'react-hot-toast';
import TableView from '../TableView';
import { QuickActionsMenu } from '../common';
import EditableTextCell from './EditableTextCell';
import CustomSelect from './CustomSelect';
import ClearMonthModal from './ClearMonthModal';
import ConfirmDeleteModal from './ConfirmDeleteModal';
import TendenciaAnualModal from './TendenciaAnualModal';
import GastosPorCentroCosto from './GastosPorCentroCosto';
import '../AssetsView.css';
import { FaSpinner } from 'react-icons/fa';
import { SURA_BLUE, SURA_BLUE2, SURA_GREEN, SURA_TEAL, mesesVisibles } from './palette';

/* ─── Custom Tooltip ───────────────────────────────────────────── */
const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-gray-100 rounded-xl shadow-xl p-3 min-w-[140px]">
      <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-1">{label}</p>
      {payload.map(p => (
        <div key={p.name} className="flex justify-between gap-3 py-0.5">
          <span className="text-xs text-gray-600">Gasto</span>
          <span className="text-xs font-bold text-gray-900">${Number(p.value || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
        </div>
      ))}
    </div>
  );
};

const CENTROS_COSTO_OPTIONS = [
  '001 - Centro de Distribución', '002 - Share Service', '003 - Presidencia',
  '004 - Gerencia General', '005 - Finanzas', '006 - Gestion Humana',
  '007 - TI', '008 - Mercadeo', '009 - Comercial', '010 - Servicios Generales',
  '011 - Almacen', '012 - Distribucion', '013 - Seguridad Industrial',
  '014 - Calidad', '015 - Supply Chain', '016 - Comercio Exterior',
  '017 - Transporte', '018 - Trade Marketing', '019 - PCP'
];

/* ─── Select con estilo del header ────────────────────────────── */
const headerSelectStyle = {
  background: 'rgba(255,255,255,0.12)',
  color: '#fff',
  border: '1px solid rgba(255,255,255,0.25)',
  borderRadius: '6px',
  padding: '5px 10px',
  fontSize: '13px',
  fontWeight: 500,
  outline: 'none',
  cursor: 'pointer',
};

const PresupuestoImpresorasView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [concepto, setConcepto] = useState(null);
  const [impresoras, setImpresoras] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const mesesOrden = ['ENERO', 'FEBRERO', 'MARZO', 'ABRIL', 'MAYO', 'JUNIO', 'JULIO', 'AGOSTO', 'SEPTIEMBRE', 'OCTUBRE', 'NOVIEMBRE', 'DICIEMBRE'];
  const mesActual = mesesOrden[new Date().getMonth()];
  const [mesSeleccionado, setMesSeleccionado] = useState(mesActual);
  const [anioSeleccionado, setAnioSeleccionado] = useState(new Date().getFullYear());
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [tendencia, setTendencia] = useState({});

  const [uploading, setUploading] = useState(false);
  const [fileFactura, setFileFactura] = useState(null);
  const fileInputRef = useRef(null);

  // Alta manual de impresora para el mes seleccionado
  const [showAddModal, setShowAddModal] = useState(false);
  const [guardandoImpresora, setGuardandoImpresora] = useState(false);
  const [nuevoEquipo, setNuevoEquipo] = useState('');
  const [nuevoCodigo, setNuevoCodigo] = useState('');
  const [nuevaSede, setNuevaSede] = useState('');
  const [nuevaUbicacion, setNuevaUbicacion] = useState('');
  const [nuevoDepartamento, setNuevoDepartamento] = useState('');
  const [nuevoLimite, setNuevoLimite] = useState('');
  const [nuevoPrecioBase, setNuevoPrecioBase] = useState('');
  const [nuevaTasaAdicional, setNuevaTasaAdicional] = useState('');
  // Impresora a eliminar de este mes (guardamos su id)
  const [impresoraAEliminar, setImpresoraAEliminar] = useState(null);
  const [trendItem, setTrendItem] = useState(null); // { titulo, subtitulo, data }
  const [activeActionMenu, setActiveActionMenu] = useState(null);
  const [menuAnchorRect, setMenuAnchorRect] = useState(null);

  // Tendencia anual de UNA impresora (por su código, a través de los meses)
  const openTendenciaImpresora = async (codigo, titulo, subtitulo) => {
    try {
      const { data } = await axiosInstance.get(
        `/api/presupuestos/impresoras/${id}/tendencia?anio=${anioSeleccionado}&codigo=${encodeURIComponent(codigo)}`
      );
      const arr = mesesVisibles(anioSeleccionado)
        .filter(mes => data[mes] != null)
        .map(mes => ({ name: mes.substring(0, 3), fullMonth: mes, total: Number(data[mes] || 0) }));
      setTrendItem({ titulo, subtitulo, data: arr });
    } catch (e) {
      toast.error('Error al cargar la tendencia');
    }
  };

  useEffect(() => {
    fetchData();
  }, [id, mesSeleccionado, anioSeleccionado]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [conceptosRes, impresorasRes, tendenciaRes] = await Promise.all([
        axiosInstance.get('/api/presupuestos/conceptos'),
        axiosInstance.get(`/api/presupuestos/impresoras/${id}?mes=${mesSeleccionado}&anio=${anioSeleccionado}`),
        axiosInstance.get(`/api/presupuestos/impresoras/${id}/tendencia?anio=${anioSeleccionado}`)
      ]);
      const currentConcepto = conceptosRes.data.find(c => c.id === parseInt(id));
      setConcepto(currentConcepto);
      setImpresoras(impresorasRes.data);
      setTendencia(tendenciaRes.data || {});
    } catch (error) {
      console.error("Error fetching impresoras data:", error);
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const resetFormAgregar = () => {
    setNuevoEquipo(''); setNuevoCodigo(''); setNuevaSede(''); setNuevaUbicacion(''); setNuevoDepartamento('');
    setNuevoLimite(''); setNuevoPrecioBase(''); setNuevaTasaAdicional('');
  };

  const handleAgregarImpresora = async () => {
    if (!nuevoEquipo.trim()) { toast.error('El nombre del equipo es requerido'); return; }
    setGuardandoImpresora(true);
    try {
      await axiosInstance.post(`/api/presupuestos/impresoras/${id}/agregar`, {
        mes: mesSeleccionado,
        anio: anioSeleccionado,
        equipo: nuevoEquipo,
        codigo: nuevoCodigo,
        sede: nuevaSede,
        nombre_it: nuevaUbicacion,
        departamento: nuevoDepartamento,
        limite_paginas: nuevoLimite ? parseInt(nuevoLimite, 10) : null,
        precio_base: nuevoPrecioBase ? parseFloat(nuevoPrecioBase) : 0,
        costo_adicional_rate: nuevaTasaAdicional ? parseFloat(nuevaTasaAdicional) : null
      });
      toast.success(`Impresora agregada a ${mesSeleccionado} ${anioSeleccionado}`);
      setShowAddModal(false);
      resetFormAgregar();
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Error al agregar la impresora');
    } finally {
      setGuardandoImpresora(false);
    }
  };

  const handleDeleteImpresora = async (impresoraId) => {
    try {
      await axiosInstance.delete(`/api/presupuestos/impresoras/${impresoraId}`);
      toast.success(`Impresora eliminada de ${mesSeleccionado} ${anioSeleccionado}`);
      fetchData();
    } catch (err) {
      toast.error('Error al eliminar');
    } finally {
      setImpresoraAEliminar(null);
    }
  };

  const handleClearMonth = () => {
    setIsDeleteModalOpen(true);
  };

  const confirmClearMonth = async () => {
    try {
      await axiosInstance.delete(`/api/presupuestos/clear/impresoras/${id}?mes=${mesSeleccionado}&anio=${anioSeleccionado}`);
      toast.success(`Datos de ${mesSeleccionado} eliminados correctamente.`);
      setIsDeleteModalOpen(false);
      fetchData();
    } catch (err) {
      toast.error('Error al eliminar los datos del mes');
    }
  };

  // Refresco SILENCIOSO: vuelve a traer impresoras y tendencia sin mostrar el
  // spinner de carga (no toca `loading`), para que editar una celda actualice
  // los totales sin recargar/parpadear la pantalla.
  const refreshSilent = async () => {
    try {
      const [impresorasRes, tendenciaRes] = await Promise.all([
        axiosInstance.get(`/api/presupuestos/impresoras/${id}?mes=${mesSeleccionado}&anio=${anioSeleccionado}`),
        axiosInstance.get(`/api/presupuestos/impresoras/${id}/tendencia?anio=${anioSeleccionado}`)
      ]);
      setImpresoras(impresorasRes.data);
      setTendencia(tendenciaRes.data || {});
    } catch (e) { /* silencioso: no interrumpir la edición */ }
  };

  // Guardar un campo de la IMPRESORA (equipo, código, sede, departamento,
  // límite, etc.). Refresca en silencio para que los totales queden al día.
  const saveImpresoraField = async (idImp, campo, valor) => {
    try {
      await axiosInstance.put(`/api/presupuestos/impresoras/${idImp}`, { [campo]: valor });
      await refreshSilent();
      return true;
    } catch (err) {
      toast.error(err.response?.data?.error || 'Error al actualizar');
      return false;
    }
  };

  // Guardar un campo del GASTO del mes (contadores, consumo, alquiler,
  // adicional, total). Lo que se escribe a mano gana sobre el cálculo.
  const saveGastoField = async (gastoId, campo, valor) => {
    if (!gastoId) return false;
    try {
      await axiosInstance.put(`/api/presupuestos/impresoras/gasto/${gastoId}`, { [campo]: valor });
      await refreshSilent();
      return true;
    } catch (err) {
      toast.error(err.response?.data?.error || 'Error al actualizar');
      return false;
    }
  };

  const handleCargarFactura = async () => {
    if (!fileFactura) {
      toast.error("Seleccione un archivo de factura (PDF/JPG)");
      return;
    }
    setUploading(true);
    const formData = new FormData();
    formData.append('factura', fileFactura);
    formData.append('mes', mesSeleccionado);
    formData.append('anio', anioSeleccionado.toString());

    try {
      const res = await axiosInstance.post(`/api/presupuestos/impresoras/${id}/cargar-factura`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success(`Factura cargada. Actualizadas: ${res.data.actualizadas}, No encontradas: ${res.data.no_encontradas}`);
      setFileFactura(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.error || "Error al procesar la factura");
    } finally {
      setUploading(false);
    }
  };

  const columns = [
    {
      header: 'Equipo',
      id: 'equipo',
      cell: ({ row }) => (
        <div className="space-y-0.5">
          <EditableTextCell
            value={row.original.equipo}
            onSave={(v) => saveImpresoraField(row.original.id, 'equipo', v)}
            placeholder="Equipo"
            className="font-semibold text-gray-800"
          />
          <EditableTextCell
            value={row.original.codigo}
            onSave={(v) => saveImpresoraField(row.original.id, 'codigo', v)}
            placeholder="Código / serial"
            className="text-xs text-gray-400"
          />
        </div>
      )
    },
    {
      header: 'Ubicación',
      id: 'ubicacion',
      cell: ({ row }) => (
        <div className="space-y-0.5">
          {/* Arriba: sede (principal). Abajo: ubicación (detalle). Ambas editables. */}
          <EditableTextCell
            value={row.original.sede}
            onSave={(v) => saveImpresoraField(row.original.id, 'sede', v)}
            placeholder="Sede"
            className="font-medium text-gray-700"
          />
          <EditableTextCell
            value={row.original.nombre_it}
            onSave={(v) => saveImpresoraField(row.original.id, 'nombre_it', v)}
            placeholder="Ubicación"
            className="text-xs text-gray-400 uppercase"
          />
        </div>
      )
    },
    {
      header: 'Departamento (CC)',
      id: 'departamento',
      cell: ({ row }) => (
        <EditableTextCell
          value={row.original.departamento}
          onSave={(val) => saveImpresoraField(row.original.id, 'departamento', val)}
          placeholder="Seleccionar..."
          options={CENTROS_COSTO_OPTIONS}
        />
      )
    },
    {
      header: 'Alquiler Base',
      id: 'alquiler',
      cell: ({ row }) => (
        <EditableTextCell
          type="number" prefix="$" decimals={2}
          value={row.original.gasto?.alquiler_dolar}
          onSave={(v) => saveGastoField(row.original.gasto?.id, 'alquiler_dolar', v)}
          className="text-gray-700 font-semibold"
        />
      )
    },
    {
      header: 'Límite',
      id: 'limite_paginas',
      cell: ({ row }) => (
        <EditableTextCell
          type="number"
          value={row.original.limite_paginas}
          onSave={(v) => saveImpresoraField(row.original.id, 'limite_paginas', v)}
          placeholder="—"
          className="text-gray-500"
        />
      )
    },
    {
      header: 'Ctr. Inicial',
      id: 'contador_inicial',
      cell: ({ row }) => (
        <EditableTextCell
          type="number"
          value={row.original.gasto?.contador_inicial}
          onSave={(v) => saveGastoField(row.original.gasto?.id, 'contador_inicial', v)}
          placeholder="—"
        />
      )
    },
    {
      header: 'Ctr. Final',
      id: 'contador_final',
      cell: ({ row }) => (
        <EditableTextCell
          type="number"
          value={row.original.gasto?.contador_final}
          onSave={(v) => saveGastoField(row.original.gasto?.id, 'contador_final', v)}
          placeholder="—"
        />
      )
    },
    {
      header: 'Consumo',
      id: 'consumo',
      cell: ({ row }) => {
        const exc = row.original.gasto?.excedente || 0;
        const lim = row.original.limite_paginas || 0;
        return (
          <div className="flex flex-col gap-0.5">
            <EditableTextCell
              type="number"
              value={row.original.gasto?.consumo}
              onSave={(v) => saveGastoField(row.original.gasto?.id, 'consumo', v)}
              placeholder="—"
            />
            {lim > 0 && row.original.gasto?.consumo != null && (
              <span className={`text-xs font-mono ${exc > 0 ? 'font-bold text-red-500' : 'text-gray-300'}`}>
                exc: {Number(exc).toLocaleString('de-DE')}
              </span>
            )}
          </div>
        );
      }
    },
    {
      header: 'Adicional',
      id: 'adicional',
      cell: ({ row }) => (
        <EditableTextCell
          type="number" prefix="$" decimals={2}
          value={row.original.gasto?.costo_adicional_dolar}
          onSave={(v) => saveGastoField(row.original.gasto?.id, 'costo_adicional_dolar', v)}
          className="text-gray-500"
        />
      )
    },
    {
      header: 'Total',
      id: 'total',
      cell: ({ row }) => (
        <EditableTextCell
          type="number" prefix="$" decimals={2}
          value={row.original.gasto?.monto_dolar}
          onSave={(v) => saveGastoField(row.original.gasto?.id, 'monto_dolar', v)}
          className="font-bold text-green-600"
        />
      )
    },
    {
      header: '',
      id: 'acciones',
      cell: ({ row }) => (
        <div className="quick-actions-container" style={{ position: 'relative' }}>
          <button
            className={`icon-button ${activeActionMenu === row.id ? 'active' : ''}`}
            onClick={(e) => {
              e.stopPropagation();
              if (activeActionMenu === row.id) {
                setActiveActionMenu(null);
                setMenuAnchorRect(null);
              } else {
                setMenuAnchorRect(e.currentTarget.getBoundingClientRect());
                setActiveActionMenu(row.id);
              }
            }}
          >
            <FaEllipsisH color="#111827" />
          </button>

          {activeActionMenu === row.id && (
            <QuickActionsMenu anchorRect={menuAnchorRect}>
              {[
                {
                  icon: TrendingUp,
                  label: 'Ver tendencia anual',
                  color: SURA_BLUE2,
                  onClick: () => {
                    openTendenciaImpresora(row.original.codigo, row.original.equipo || row.original.codigo, row.original.codigo);
                    setActiveActionMenu(null);
                  }
                },
                {
                  icon: Trash2,
                  label: 'Eliminar Impresora',
                  color: '#EF4444',
                  onClick: () => {
                    setImpresoraAEliminar(row.original.id);
                    setActiveActionMenu(null);
                  }
                }
              ].map((action) => (
                <button key={action.label} className="quick-action-item" onClick={action.onClick}>
                  <span className="action-icon" style={{ color: action.color }}><action.icon size={15} /></span>
                  <span className="action-label">{action.label}</span>
                </button>
              ))}
            </QuickActionsMenu>
          )}
        </div>
      )
    }
  ];

  /* Serie anual: viene del backend (cada impresora vive en un solo mes, así que
     el total por mes se calcula del lado del servidor). */
  const chartData = useMemo(() => {
    return mesesVisibles(anioSeleccionado).map(mes => ({
      name: mes.substring(0, 3),
      fullMonth: mes,
      total: Number(tendencia[mes] || 0)
    }));
  }, [tendencia, anioSeleccionado]); // eslint-disable-line

  /* Gastos por centro de costo (departamento) del mes/año seleccionado
     — impresoras y su .gasto ya vienen filtrados por mes/año desde el backend */
  const ccData = useMemo(() => {
    const map = {};
    impresoras.forEach(imp => {
      const monto = imp.gasto?.monto_dolar;
      if (!monto) return;
      const cc = imp.departamento || imp.sede || 'Sin asignar';
      map[cc] = (map[cc] || 0) + Number(monto);
    });
    return Object.entries(map)
      .map(([centro_costo, total]) => ({ centro_costo, total }))
      .sort((a, b) => b.total - a.total);
  }, [impresoras]);

  if (loading && !concepto) {
    return (
      <div className="flex justify-center items-center h-64">
        <FaSpinner className="animate-spin text-3xl" style={{ color: SURA_BLUE }} />
      </div>
    );
  }

  const totalAlquiler = impresoras.reduce((sum, item) => sum + (item.gasto?.alquiler_dolar || 0), 0);
  const totalMonto = impresoras.reduce((sum, item) => sum + (item.gasto?.monto_dolar || 0), 0);
  const totalConsumo = impresoras.reduce((sum, item) => sum + (item.gasto?.consumo || 0), 0);

  return (
    <div className="assets-view">

      {/* ── Header compacto (igual que Telefonía) ─────────────── */}
      <div
        className="rounded-xl mb-6 px-5 py-0 flex items-center justify-between gap-3 relative"
        style={{
          background: `linear-gradient(135deg, ${SURA_BLUE} 0%, ${SURA_BLUE2} 100%)`,
          minHeight: '64px',
        }}
      >
        {/* Círculo decorativo */}
        <div className="absolute inset-0 overflow-hidden rounded-xl pointer-events-none">
          <div className="absolute right-0 top-0 w-40 h-40 rounded-full pointer-events-none"
            style={{ background: 'rgba(255,255,255,0.06)', transform: 'translate(30%, -30%)' }} />
        </div>

        {/* Izquierda: nav + ícono + título */}
        <div className="flex items-center gap-3 z-10 py-3 flex-shrink-0">
          <button
            onClick={() => navigate('/presupuestos')}
            className="flex items-center gap-1 transition-opacity hover:opacity-80"
            style={{ color: 'rgba(255,255,255,0.65)', fontSize: '13px', fontWeight: 500 }}
          >
            <ChevronLeft size={15} />
            Presupuestos
          </button>
          <div style={{ width: '1px', height: '20px', background: 'rgba(255,255,255,0.2)' }} />
          <div
            className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
            style={{ background: 'rgba(255,255,255,0.15)' }}
          >
            <Printer size={18} color="#fff" />
          </div>
          <div>
            <div className="text-white font-bold text-sm leading-tight">
              {concepto?.nombre || 'Impresoras'} — Control de Impresoras
            </div>
            <div style={{ color: 'rgba(255,255,255,0.55)', fontSize: '11px' }}>
              Gestión de alquileres y contadores de impresión
            </div>
          </div>
        </div>

        {/* Derecha: controles integrados */}
        <div className="flex items-center gap-2 z-10 flex-wrap justify-end py-3">
          {/* Mes */}
          <CustomSelect value={mesSeleccionado} onChange={e => setMesSeleccionado(e.target.value)} style={headerSelectStyle}>
            {mesesOrden.map(m => <option key={m} value={m}>{m}</option>)}
          </CustomSelect>

          {/* Año */}
          <CustomSelect value={anioSeleccionado} onChange={e => setAnioSeleccionado(Number(e.target.value))} style={headerSelectStyle}>
            {[2024, 2025, 2026].map(y => <option key={y} value={y}>{y}</option>)}
          </CustomSelect>

          <button
            onClick={handleClearMonth}
            className="bg-red-500/20 hover:bg-red-500 text-white p-1.5 rounded-lg transition-colors border border-red-500/30 hover:border-red-500 flex items-center justify-center group"
            title="Limpiar datos del mes"
            style={{ width: '34px', height: '34px' }}
          >
            <Trash2 size={16} className="text-red-300 group-hover:text-white transition-colors" />
          </button>

          {/* Input archivo oculto */}
          <input
            type="file"
            ref={fileInputRef}
            accept=".pdf,.jpg,.jpeg,.png"
            onChange={(e) => setFileFactura(e.target.files[0])}
            className="hidden"
          />

          {/* Botón seleccionar archivo */}
          <button
            onClick={() => fileInputRef.current.click()}
            className="flex items-center gap-1.5 transition-all"
            style={{
              background: 'rgba(255,255,255,0.12)',
              color: fileFactura ? '#FFD100' : 'rgba(255,255,255,0.8)',
              border: `1px solid ${fileFactura ? 'rgba(255,209,0,0.5)' : 'rgba(255,255,255,0.25)'}`,
              borderRadius: '6px',
              padding: '5px 12px',
              fontSize: '12px',
              fontWeight: 600,
            }}
          >
            <FaFileAlt size={11} />
            {fileFactura
              ? <span style={{ maxWidth: '120px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{fileFactura.name}</span>
              : 'Seleccionar Archivo'}
          </button>

          {/* Botón subir factura */}
          <button
            onClick={handleCargarFactura}
            disabled={uploading || !fileFactura}
            className="flex items-center gap-1.5 transition-all"
            style={{
              background: fileFactura ? 'rgba(255,255,255,0.22)' : 'rgba(255,255,255,0.06)',
              color: fileFactura ? '#fff' : 'rgba(255,255,255,0.35)',
              border: `1px solid ${fileFactura ? 'rgba(255,255,255,0.4)' : 'rgba(255,255,255,0.12)'}`,
              borderRadius: '6px',
              padding: '5px 12px',
              fontSize: '12px',
              fontWeight: 600,
              cursor: fileFactura ? 'pointer' : 'not-allowed',
            }}
          >
            {uploading ? <FaSpinner className="animate-spin" size={11} /> : <FaUpload size={11} />}
            {uploading ? 'Procesando...' : 'Extraer con IA'}
          </button>

          {/* Botón agregar impresora manual */}
          <button
            onClick={() => setShowAddModal(true)}
            className="flex items-center gap-1.5 transition-all"
            style={{
              background: 'rgba(255,255,255,0.25)',
              color: '#fff',
              border: '1px solid rgba(255,255,255,0.4)',
              borderRadius: '6px',
              padding: '5px 12px',
              fontSize: '12px',
              fontWeight: 600,
            }}
          >
            <FaPlus size={11} />
            Agregar Impresora
          </button>

          {/* Badge tipo */}
          <span
            className="text-xs font-bold px-3 py-1 rounded-full"
            style={{
              background: 'rgba(255,255,255,0.15)',
              color: '#fff',
              border: '1px solid rgba(255,255,255,0.25)',
              letterSpacing: '0.05em',
            }}
          >
            IMPRESORAS
          </span>
        </div>
      </div>

      {/* ── KPI Row ──────────────────────────────────────────────── */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
        {[
          { title: 'Equipos Activos', value: impresoras.length, icon: LayoutGrid, color: SURA_BLUE, bg: '#EFF6FF', mono: false },
          { title: 'Consumo General', value: totalConsumo.toLocaleString('de-DE'), icon: Printer, color: SURA_BLUE, bg: '#EFF6FF', mono: true },
          { title: 'Alquiler Base', value: `$${totalAlquiler.toFixed(2)}`, icon: Wallet, color: SURA_BLUE2, bg: '#EFF6FF', mono: true },
          { title: 'Gasto Total del Mes', value: `$${totalMonto.toFixed(2)}`, icon: Wallet, color: SURA_GREEN, bg: '#ECFDF5', mono: true },
        ].map(kpi => (
          <div key={kpi.title} className="bg-white rounded-xl border border-gray-100 shadow-sm p-5 relative overflow-hidden hover:shadow-md transition-shadow">
            <div className="absolute top-0 left-0 right-0 h-0.5 rounded-t-xl" style={{ background: kpi.color }} />
            <div className="flex justify-between items-start mb-3">
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">{kpi.title}</p>
              <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: kpi.bg }}>
                <kpi.icon size={17} style={{ color: kpi.color }} />
              </div>
            </div>
            <p className={`text-2xl font-bold text-gray-900 ${kpi.mono ? 'font-mono' : ''}`}>{kpi.value}</p>
          </div>
        ))}

        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 relative overflow-hidden hover:shadow-md transition-shadow">
          <div className="absolute top-0 left-0 right-0 h-0.5 rounded-t-xl" style={{ background: SURA_BLUE }} />
          <div className="flex justify-between items-center mb-1">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Tendencia Anual</p>
            <TrendingUp size={14} style={{ color: SURA_TEAL }} />
          </div>
          <div className="h-16 w-full mt-2">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chartData} margin={{ top: 5, right: 0, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="gradImpresoras" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={SURA_TEAL} stopOpacity={0.25} />
                    <stop offset="95%" stopColor={SURA_TEAL} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <XAxis dataKey="name" tick={{ fontSize: 9, fill: '#9ca3af' }} axisLine={false} tickLine={false} interval="preserveStartEnd" />
                <Tooltip content={<CustomTooltip />} />
                <Area type="monotone" dataKey="total" stroke={SURA_TEAL} strokeWidth={2} fill="url(#gradImpresoras)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* ── Gastos por Centro de Costo ─────────────────────────── */}
      <div className="mb-6">
        <GastosPorCentroCosto
          data={ccData}
          subtitle={`${mesSeleccionado.charAt(0) + mesSeleccionado.slice(1).toLowerCase()} ${anioSeleccionado}`}
        />
      </div>

      {/* ── Tabla ──────────────────────────────────────────────── */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        {loading ? (
          <div className="flex justify-center items-center h-48">
            <FaSpinner className="animate-spin text-2xl" style={{ color: SURA_BLUE }} />
          </div>
        ) : (
          <TableView
            data={[...impresoras].sort((a, b) => (b.gasto?.monto_dolar || 0) - (a.gasto?.monto_dolar || 0))}
            columns={columns}
            searchPlaceholder="Buscar por equipo, serie, sede..."
          />
        )}
      </div>

      <ClearMonthModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={confirmClearMonth}
        mes={mesSeleccionado}
        anio={anioSeleccionado}
        tipo="Impresoras"
        nota="Solo se borran los datos cargados del mes (contadores, alquiler y montos). Las impresoras del mes NO se eliminan — para quitar una, usa la papelera de su fila."
      />

      <ConfirmDeleteModal
        isOpen={impresoraAEliminar !== null}
        onClose={() => setImpresoraAEliminar(null)}
        onConfirm={() => handleDeleteImpresora(impresoraAEliminar)}
        title="Quitar impresora"
        message={`Se quitará esta impresora únicamente de ${mesSeleccionado} ${anioSeleccionado}. Los demás meses no se ven afectados.`}
      />

      <TendenciaAnualModal
        isOpen={trendItem !== null}
        onClose={() => setTrendItem(null)}
        titulo={trendItem?.titulo}
        subtitulo={trendItem?.subtitulo}
        anio={anioSeleccionado}
        data={trendItem?.data || []}
      />

      {/* ── Modal: Agregar Impresora ───────────────────────────── */}
      {showAddModal && createPortal(
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
          <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl overflow-hidden">
            <div className="px-6 py-5 flex justify-between items-center" style={{ background: `linear-gradient(135deg, ${SURA_BLUE} 0%, ${SURA_BLUE2} 100%)` }}>
              <h3 className="text-white font-bold m-0 flex items-center gap-2">
                <FaPlus size={14} /> Agregar Impresora — {mesSeleccionado} {anioSeleccionado}
              </h3>
              <button onClick={() => setShowAddModal(false)} className="text-white/70 hover:text-white text-lg font-bold">✕</button>
            </div>
            <div className="p-6 space-y-4 max-h-[70vh] overflow-y-auto">
              <p className="text-xs text-gray-400 -mt-1">
                Se agregará únicamente para {mesSeleccionado} {anioSeleccionado}. No aparecerá en otros meses.
              </p>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Equipo <span className="text-red-500">*</span></label>
                <input type="text" placeholder="Ej: CANON IR ADV 525" className="w-full px-3 py-2 border border-gray-200 rounded-lg outline-none focus:border-blue-400 transition-colors" value={nuevoEquipo} onChange={e => setNuevoEquipo(e.target.value)} />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Código / Serial</label>
                <input type="text" placeholder="Opcional" className="w-full px-3 py-2 border border-gray-200 rounded-lg outline-none focus:border-blue-400 transition-colors" value={nuevoCodigo} onChange={e => setNuevoCodigo(e.target.value)} />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Sede</label>
                <input type="text" placeholder="Ej: CDN, CA1..." className="w-full px-3 py-2 border border-gray-200 rounded-lg outline-none focus:border-blue-400 transition-colors" value={nuevaSede} onChange={e => setNuevaSede(e.target.value)} />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Ubicación</label>
                <input type="text" placeholder="Ej: ROMANA-F, PISO 6..." className="w-full px-3 py-2 border border-gray-200 rounded-lg outline-none focus:border-blue-400 transition-colors" value={nuevaUbicacion} onChange={e => setNuevaUbicacion(e.target.value)} />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Departamento (CC)</label>
                <CustomSelect className="w-full px-3 py-2 border border-gray-200 rounded-lg outline-none focus:border-blue-400" value={nuevoDepartamento} onChange={e => setNuevoDepartamento(e.target.value)}>
                  <option value="">Sin asignar (se puede editar después)</option>
                  {CENTROS_COSTO_OPTIONS.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                </CustomSelect>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Precio base ($)</label>
                  <input type="number" step="0.01" placeholder="0.00" className="w-full px-3 py-2 border border-gray-200 rounded-lg outline-none focus:border-blue-400 transition-colors" value={nuevoPrecioBase} onChange={e => setNuevoPrecioBase(e.target.value)} />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Límite páginas</label>
                  <input type="number" placeholder="Opcional" className="w-full px-3 py-2 border border-gray-200 rounded-lg outline-none focus:border-blue-400 transition-colors" value={nuevoLimite} onChange={e => setNuevoLimite(e.target.value)} />
                </div>
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Tarifa por página excedente ($)</label>
                <input type="number" step="0.0001" placeholder="Opcional" className="w-full px-3 py-2 border border-gray-200 rounded-lg outline-none focus:border-blue-400 transition-colors" value={nuevaTasaAdicional} onChange={e => setNuevaTasaAdicional(e.target.value)} />
              </div>
            </div>
            <div className="px-6 py-4 bg-gray-50 flex justify-end gap-3 border-t border-gray-100">
              <button onClick={() => setShowAddModal(false)} className="px-5 py-2 text-sm font-bold text-gray-600 bg-white border border-gray-200 rounded-xl hover:bg-gray-100">Cancelar</button>
              <button
                onClick={handleAgregarImpresora}
                disabled={guardandoImpresora}
                className="px-5 py-2 text-sm font-bold text-white rounded-xl shadow-md hover:shadow-lg transition-all disabled:opacity-60"
                style={{ background: `linear-gradient(135deg, ${SURA_BLUE} 0%, ${SURA_BLUE2} 100%)` }}
              >
                {guardandoImpresora ? 'Agregando...' : 'Agregar Impresora'}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default PresupuestoImpresorasView;
